import React, { useState, useEffect, useRef } from 'react';
import { XMarkIcon, CheckCircleIcon, InformationCircleIcon } from './Icons';
import { CIEN_CUADRAS_BUTTON_BLUE } from '../firebase/firebaseConfig';

interface CopyFallbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  textToCopy: string;
  title: string;
}

type CopyStatus = 'idle' | 'success' | 'fallback';

export const CopyFallbackModal: React.FC<CopyFallbackModalProps> = ({ isOpen, onClose, textToCopy, title }) => {
  const [copyStatus, setCopyStatus] = useState<CopyStatus>('idle');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      // Attempt to copy automatically when the modal opens
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(textToCopy)
          .then(() => {
            setCopyStatus('success');
            // Auto-close on success after a short delay
            setTimeout(onClose, 2000);
          })
          .catch(() => {
            // This is the fallback for iframe environments or permissions issues
            setCopyStatus('fallback');
          });
      } else {
        // Fallback for older browsers or insecure contexts
        setCopyStatus('fallback');
      }
    } else {
      // Reset status when modal is closed
      setCopyStatus('idle');
    }
  }, [isOpen, textToCopy, onClose]);

  // Effect to select text when in fallback mode
  useEffect(() => {
    if (copyStatus === 'fallback' && inputRef.current) {
      inputRef.current.select();
      inputRef.current.focus();
    }
  }, [copyStatus]);
  
  if (!isOpen) {
    return null;
  }

  return (
    <div 
      className="fixed inset-0 bg-gray-600 bg-opacity-75 z-[300] flex items-center justify-center p-4" 
      style={{ margin: 0, marginTop: 0, top: 0, paddingTop: 0 }}
      role="dialog" 
      aria-modal="true" 
      aria-labelledby="copy-modal-title"
    >
      <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full">
        <header className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h3 id="copy-modal-title" className="text-lg font-medium leading-6 text-gray-900">
            {title}
          </h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="w-6 h-6" />
          </button>
        </header>
        
        <div className="p-6">
          {copyStatus === 'success' ? (
            <div className="text-center py-4">
              <CheckCircleIcon className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <p className="font-semibold text-gray-800">¡Copiado al portapapeles!</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-sky-50 border-l-4 border-sky-500 p-3 text-sky-800 text-sm flex items-start">
                <InformationCircleIcon className="w-5 h-5 mr-3 flex-shrink-0" />
                <span>
                    El copiado automático no está disponible. Por favor, copia el texto manualmente.
                </span>
              </div>
              <div>
                <label htmlFor="text-to-copy-input" className="sr-only">Texto a copiar</label>
                <input
                  ref={inputRef}
                  id="text-to-copy-input"
                  type="text"
                  value={textToCopy}
                  readOnly
                  className="w-full bg-gray-100 border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-sky-500"
                />
              </div>
               <p className="text-center text-sm font-semibold text-gray-700">
                Presiona <kbd className="px-2 py-1.5 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded-lg">Ctrl + C</kbd> o <kbd className="px-2 py-1.5 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded-lg">Cmd + C</kbd> para copiar.
              </p>
            </div>
          )}
        </div>

        <footer className="bg-gray-50 px-6 py-3 border-t flex justify-end">
           <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50"
            >
              Cerrar
            </button>
        </footer>
      </div>
    </div>
  );
};